<template>
  <!-- eslint-disable -->
  <PinturaEditor
    id="step1"
    ref="pinturaEditor"
    v-bind="editorDefaults"
    :src="src"
    :util="util"
    :imageAnnotation="positionAreaAnnotation"
    :imageDecoration="activePositionShapes"
    :preview-upscale="true"
    :markupEditorZoomLevels="[
      0.1, 0.15, 0.2, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3,
    ]"
    :markupEditorZoomAdjustStep="0.01"
    :markupEditorZoomAdjustFactor="0.1"
    :beforeUpdateShape="beforeUpdateShape"
    :beforeSelectShape="beforeSelectShape"
    :beforeDeselectShape="beforeDeselectShape"
    :beforeRemoveShape="beforeRemoveShape"
    @pintura:updateshape="updateShape"
    @pintura:zoom="(e) => $emit('action:zoom', e)"
    @select-shape="selectShape"
  />
</template>
<script>
import Vue from 'vue';

import { limitToContextWithCorrectionFactor } from '@/utils/common';
import { strUndo24, strRedo24, strCursor24, strTouch24 } from './carbon-icons';
import {
  /**
   * Import the default image reader and writer
   */
  createDefaultImageReader,
  createDefaultImageWriter,

  /**
   * The method used to register the plugins
   */
  setPlugins,

  /**
   * The plugins we want to use
   */
  plugin_decorate,
  plugin_crop,
  plugin_annotate,

  /**
   * The user interface and plugin locale objects
   */
  locale_en_gb,
  plugin_decorate_locale_en_gb,

  /**
   * Because we use the annotate plugin we also need to import the markup editor
   * locale
   */
  markup_editor_locale_en_gb,

  /**
   * Import the default configuration for the markup editor and finetune
   * plugins markup_editor_defaults,
   */
  createNode,
  findNode,
  colorStringToColorArray,
  insertNodeBefore,
  insertNodeAfter,
} from 'pintura';

import { PinturaEditor } from '@/components/w2pPintura/PinturaEditor';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { mapState, mapActions } from 'pinia';
import { lineWidthV2 } from '@/constants/areaLineWidth';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import cloneDeep from 'lodash.clonedeep';
import { createStateSnapshot } from '@/utils/historyHelperV2';
import { Category } from '@midocean/webapps-shared-domain';
import {
  generateTargetCoordinates,
  getActivePanelNumber,
} from '@/utils/drawPanelLines';
import {
  drawPanelSections,
  generatePanelSections,
  updateTargetLines,
} from '@/utils/roundItemsHelper';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';

setPlugins(plugin_decorate, plugin_crop, plugin_annotate);

export default Vue.extend({
  name: 'W2pPintura',
  components: {
    PinturaEditor,
  },
  data() {
    return {
      src: require('../../../../assets/resizedCheckerTransparency.png'),
      activeInteractionMode: 'auto',
      util: 'finetune',
      // Our editor custom configuration options
      editorDefaults: {
        imageReader: createDefaultImageReader(),
        imageWriter: createDefaultImageWriter(),
        stickerEnableSelectImage: false,
        // NOTE: To enable the shapes from the annotation array to be selectable try setting this to something different
        annotateActiveTool: 'rectangle',
        // NOTE: To enable the shapes from the annotation array to be selectable try setting this to something different
        utils: ['decorate'],
        decorateTools: [],
        decorateActiveTool: 'move',
        enableToolbar: true,
        enableButtonExport: false,
        enableButtonRevert: false,
        enableNavigateHistory: false,

        // The icons and labels to use in the user interface (required)
        locale: {
          ...locale_en_gb,
          ...plugin_decorate_locale_en_gb,
          ...markup_editor_locale_en_gb,
        },
        willRenderShapeControls: (controls, _selectedShapeId) => {
          const alphaNode = findNode('beta', controls);
          const shape = this.shapes.find(
            (shape) => shape.id === _selectedShapeId
          );

          const isTextShape = shape?.w2pText !== undefined;

          if (isTextShape) {
            alphaNode[3].forEach((tool, index) => {
              alphaNode[3][index] = false;
            });
          }
        },
        willRenderToolbar: (toolbar, env, redraw) => {
          const betaNode = findNode('beta', toolbar);

          if (
            betaNode[3] &&
            betaNode[3].length > 0 &&
            betaNode[3][1].length > 0
          ) {
            insertNodeBefore(
              createNode('Button', 'button-undo', {
                icon: strUndo24,
                disabled: true,
                title: this.$t('wizard-v2.toolbar.history-back'),
                class: 'button-undo',
                onclick: () => {
                  this.undoHandler();
                },
              }),
              'plugin-tools',
              betaNode[3]
            );

            insertNodeBefore(
              createNode('Button', 'button-redo', {
                icon: strRedo24,
                disabled: true,
                title: this.$t('wizard-v2.toolbar.history-forward'),
                class: 'button-redo',
                onclick: () => {
                  this.redoHandler();
                },
              }),
              'plugin-tools',
              betaNode[3]
            );

            insertNodeAfter(
              createNode('Button', 'button-select', {
                icon: strTouch24,
                title: this.$t('wizard-v2.toolbar.pan-mode'),
                onclick: () => {
                  this.activeInteractionMode = 'pan';
                  this.pintura.editor.markupEditorInteractionMode = 'pan';
                },
              }),
              'plugin-tools',
              betaNode[3]
            );

            insertNodeAfter(
              createNode('Button', 'button-pan', {
                icon: strCursor24,
                title: this.$t('wizard-v2.toolbar.select-mode'),
                onclick: () => {
                  this.activeInteractionMode = 'auto';
                  this.pintura.editor.markupEditorInteractionMode = 'auto';
                },
              }),
              'plugin-tools',
              betaNode[3]
            );
          }

          return [...toolbar];
        },
        willRenderCanvas: (shapes, state) => {
          const { selectionRect } = state;

          if (this.selectedPrintPosition.id) {
            this.$emit('pintura:selection', selectionRect);
          }
          return shapes;
        },
      },
    };
  },

  computed: {
    ...mapState(useProductStore, [
      'activePosition',
      'selectedPrintPosition',
      'getProductPrintPosition',
      'setPanelPosition',
      'centerCoordinatesOfPanels',
      'panelsPositions',
    ]),
    ...mapState(useShapeStore, [
      'positionShapes',
      'shapes',
      'shapeById',
      'activeShapeId',
    ]),
    ...mapState(useMirrorStore, ['getActiveMirrorShapes']),
    ...mapState(useHistoryStore, ['isUndoPossible', 'isRedoPossible']),

    positionAreaAnnotation() {
      if (this.activePosition && this.activePosition.printArea) {
        if (this.activePosition.printArea.shapeType === 'Rectangle') {
          const {
            x,
            y,
            width,
            height,
            rotation,
            isLanyard,
            distanceOfMirrorPx,
          } = this.printPositionArea(this.activePosition.id, true);

          if (isLanyard) {
            return [
              {
                id: this.activePosition.id,
                strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
                strokeWidth: `${lineWidthV2}%`,
                rotation,
                x,
                y,
                width,
                height,
              },
              {
                id: this.activePosition.id,
                strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
                strokeWidth: `${lineWidthV2}%`,
                rotation,
                x,
                y: y + distanceOfMirrorPx,
                width,
                height,
              },
            ];
          } else if (this.activePosition.category === Category.RoundItem) {
            const annotationShapes = [];

            annotationShapes.push(
              ...drawPanelSections(this.activePosition, y, height)
            );
            this.centerCoordinatesOfPanels.forEach((element) => {
              annotationShapes.push(element);
            });

            return [
              ...annotationShapes,
              {
                id: this.activePosition.id,
                strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
                strokeWidth: `${lineWidthV2}%`,
                rotation,
                x,
                y,
                width,
                height,
                dashedLine: [10, 10],
                lineDash: [10, 10],
              },
            ];
          } else {
            return [
              {
                id: this.activePosition.id,
                strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
                strokeWidth: `${lineWidthV2}%`,
                rotation,
                x,
                y,
                width,
                height,
                dashedLine: [10, 10],
                lineDash: [10, 10],
              },
            ];
          }
        }

        if (this.activePosition.printArea.shapeType === 'Ellipse') {
          const { x, y, rx, ry, rotation } = this.printPositionArea(
            this.activePosition.id,
            true
          );

          return [
            {
              id: this.activePosition.id,
              strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
              strokeWidth: `${lineWidthV2}%`,
              rotation,
              x,
              y,
              rx,
              ry,
            },
          ];
        }

        if (this.activePosition.printArea.shapeType === 'Polygon') {
          const { points } = this.printPositionArea(
            this.activePosition.id,
            true
          );

          return [
            {
              id: this.activePosition.id,
              strokeColor: colorStringToColorArray('rgba(36, 161, 72, 1)'),
              strokeWidth: `${lineWidthV2}%`,
              rotation: 0,
              points,
            },
          ];
        }
      }

      return [];
    },

    activePositionShapes() {
      if (this.activePosition && this.activePosition.id) {
        return [
          ...this.positionShapes(this.activePosition.id),
          ...this.getActiveMirrorShapes(),
        ];
      } else {
        return [];
      }
    },
  },

  watch: {
    isUndoPossible(newValue) {
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      const element =
        subtarget && subtarget.shadowRoot
          ? subtarget.shadowRoot.querySelector('.button-undo')
          : document.querySelector('.button-undo');
      if (element) {
        element.disabled = !newValue;
      }
    },
    isRedoPossible(newValue) {
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      const element =
        subtarget && subtarget.shadowRoot
          ? subtarget.shadowRoot.querySelector('.button-redo')
          : document.querySelector('.button-redo');
      if (element) {
        element.disabled = !newValue;
      }
    },
    activePosition() {
      if (!this.activePosition || !this.activePosition.id) return;

      const { x, y, width, height } = this.printPositionArea(
        this.activePosition.id,
        true
      );
      this.setPanelsPositions([
        x,
        ...generatePanelSections(this.activePosition),
        x + width,
      ]);
      for (let index = 0; index < this.panelsPositions.length; index++) {
        if (this.panelsPositions[index + 1]) {
          this.setCenterCoordinatesOfPanels([
            ...this.centerCoordinatesOfPanels,
            ...generateTargetCoordinates(
              this.panelsPositions,
              index,
              height,
              y
            ),
          ]);
        }
      }
    },
  },

  created() {
    this.$nextTick(() => {
      window.pintura = this.$refs['pinturaEditor'];
      this.pintura = window.pintura;
    });
    /**
     * Clean up editor instance to prevent memory leaks
     */
    this.$once('hook:beforeDestroy', () => {
      window.pintura = undefined;
      this.pintura = undefined;
    });
  },

  methods: {
    ...mapActions(useProductStore, [
      'printPositionArea',
      'updateProductStateFromSnapshot',
      'area',
      'setCenterCoordinatesOfPanels',
      'setPanelsPositions',
    ]),
    ...mapActions(useShapeStore, [
      'updateShapePosition',
      'deselectShape',
      'checkIsShapeInArea',
      'updateShapeStateFromSnapshot',
      'setSmallDetailsCheckToInvalid2',
    ]),
    ...mapActions(useNotificationStore, [
      'runValidators',
      'updateNotificationStateFromSnapshot',
    ]),
    ...mapActions(useHistoryStore, ['undo', 'redo', 'recordHistory']),
    ...mapActions(useMirrorStore, [
      'updateMirrorStateFromSnapshot',
      'updateMirrorShape',
    ]),

    updateShape(current) {
      /**
       * UPDATE shape x, y, rotation
       */

      if (this.activePosition.id) {
        const scaledArea = this.printPositionArea(this.activePosition.id, true);
        const productArea = this.activePosition.printArea;

        this.updateShapePosition(current, scaledArea, productArea);
        this.checkIsShapeInArea(current.id);
        this.runValidators();

        const activeShape = this.shapeById(this.activeShapeId);
        if (activeShape) {
          this.updateMirrorShape(activeShape);
        }

        const eventPayload = generateEventPayloadV2();

        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });

        const snapshot = createStateSnapshot();
        this.recordHistory(snapshot);
      }
    },

    selectShape(id) {
      this.shapes[0].isSelected = true;
    },

    beforeSelectShape(current, target) {
      this.$emit('beforeselect-shape', target);
      return true;
    },

    beforeUpdateShape(shape, props, ctx) {
      if (
        shape.smallDetailsResult &&
        (props.width !== undefined || props.height !== undefined)
      ) {
        this.setSmallDetailsCheckToInvalid2(shape.id);
      }

      updateTargetLines(
        shape,
        this.centerCoordinatesOfPanels,
        this.panelsPositions
      );

      const activePanelNumber = getActivePanelNumber(
        shape.x,
        this.panelsPositions
      );

      const panelX = this.panelsPositions[activePanelNumber - 1];
      const nextPanelPosition = this.panelsPositions[activePanelNumber];
      this.setPanelPosition({
        x: panelX,
        width: nextPanelPosition - panelX,
      });

      if (props.width !== undefined) {
        // min 1px
        // prevent logo from disappearing
        // keep aspect ratio
        props.width = props.width < 1 ? 1 : props.width;
        props.height = props.width === 1 ? shape.height : props.height;
      }

      if (props.height !== undefined) {
        // min 1px
        // prevent logo from disappearing
        // keep aspect ratio
        props.height = props.height < 1 ? 1 : props.height;
        props.width = props.height === 1 ? shape.width : props.width;
      }

      const limit = limitToContextWithCorrectionFactor(
        ctx,
        undefined, // use default offset
        props,
        shape
      );

      /**app
       * *** SPECIAL: In case we get a rotation property inside props -
       * Here x and y aren't set at all, so we need to return the orginal back
       * without any modification
       *
       * VERY IMPORTANT:
       * Bear in mind props will be of a different shape, depending on action
       * being performed
       */
      const rotation = ((shape.rotation * 180) / Math.PI) % 360;
      if ((rotation !== 0 && rotation !== 180) || props.rotation) {
        return props;
      }

      this.$emit('beforeupdate-shape', shape);

      /**
       * Otherwise apply limitation to x and y
       * cases like scaling and moving operations
       */
      return {
        ...props,
        x: limit.x,
        y: limit.y,
      };
    },

    beforeDeselectShape(current, target) {
      this.deselectShape();
      return true;
    },

    beforeRemoveShape(shape) {
      return true;
    },

    undoHandler() {
      const stateSnapshot = this.undo();

      if (stateSnapshot) {
        if (stateSnapshot.productState !== undefined) {
          this.updateProductStateFromSnapshot(
            cloneDeep(stateSnapshot.productState)
          );
        }

        if (stateSnapshot.shapeState !== undefined) {
          this.updateShapeStateFromSnapshot(
            cloneDeep(stateSnapshot.shapeState)
          );
        }

        if (stateSnapshot.notificationState !== undefined) {
          this.updateNotificationStateFromSnapshot(
            cloneDeep(stateSnapshot.notificationState)
          );
        }

        if (stateSnapshot.mirrorState !== undefined) {
          this.updateMirrorStateFromSnapshot(
            cloneDeep(stateSnapshot.mirrorState)
          );
        }

        const eventPayload = generateEventPayloadV2();
        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });
      }
    },

    redoHandler() {
      const stateSnapshot = this.redo();

      if (stateSnapshot) {
        if (stateSnapshot.productState !== undefined) {
          this.updateProductStateFromSnapshot(
            cloneDeep(stateSnapshot.productState)
          );
        }

        if (stateSnapshot.shapeState !== undefined) {
          this.updateShapeStateFromSnapshot(
            cloneDeep(stateSnapshot.shapeState)
          );
        }

        if (stateSnapshot.notificationState !== undefined) {
          this.updateNotificationStateFromSnapshot(
            cloneDeep(stateSnapshot.notificationState)
          );
        }

        if (stateSnapshot.mirrorState !== undefined) {
          this.updateMirrorStateFromSnapshot(
            cloneDeep(stateSnapshot.mirrorState)
          );
        }

        const eventPayload = generateEventPayloadV2();
        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });
      }
    },
  },
});
</script>

<style lang="scss">
@import '../../../../styles/_carbon-utils';
@import '../../../../styles/carbon-overrides.scss';
@import '../../../../../node_modules/pintura/pintura.css';

@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;0,400;0,700;1,400&display=swap');

#root {
  position: relative;
  height: 100%;

  #header {
    position: relative;
  }
}

.pintura-editor {
  --color-background: 244, 244, 244;
  --font-family: 'Roboto', sans-serif;
  --font-size: 16px;
}

.PinturaRoot {
  padding-top: 0;
  background: #e0e0e0 !important ;
}
.PinturaRoot[data-env~='landscape'] > .PinturaNavTools {
  justify-content: left;
}
.CustomTabList {
  display: none;
  background: #f4f4f4;

  .PinturaButton {
    width: 3rem;
    height: 3rem;
    border: none;
    padding: 0.1rem;
    margin: 0;
  }
}
.PinturaRoot[data-env~='landscape'] > .PinturaNavMain {
  display: none;
}
.PinturaRoot .PinturaEditorOverlay {
  background-color: rgba(0, 0, 0, 0.6);
}
.custom-shape-control.PinturaButtonIconOnly {
  width: 2.3em;
  height: 2.3em;
  svg {
    fill: white;
  }
}
.custom-bring-to-front.PinturaButtonIconOnly {
  svg {
    width: 80%;
  }
}

.import-wizard-v2 .PinturaUtilMain {
  padding-top: 80px;
}

.import-wizard-v2 .PinturaRoot .PinturaNavGroup {
  margin-top: 20px;
}
</style>

<style lang="scss">
.pintura-sticker-editor {
  --editor-modal-overlay-opacity: 0.65;
  --editor-modal-border-radius: 1.5rem;
  --editor-max-width: 48%;
  --editor-max-height: 50%;
}
</style>
